import React, { useState, useEffect } from 'react';
import { ref, get, set, push, update } from 'firebase/database';
import { realtimeDb } from '../firebase';
import './ReportCardModal.css';

interface ReportCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  childName: string;
  childClass: string;
  userType: 'teacher' | 'parent' | 'guest';
  userId: string;
  studentCode?: string; // guest 사용자를 위한 학생 코드
}

interface MonthlyReport {
  month: number;
  content: string;
  teacherId: string;
  teacherName: string;
  createdAt: string;
  reply?: string; // 학부모 한마디를 reply 필드로 저장
  replyAuthor?: string;
  replyCreatedAt?: string;
}

const ReportCardModal: React.FC<ReportCardModalProps> = ({ 
  isOpen, 
  onClose, 
  childName, 
  childClass,
  userType,
  userId,
  studentCode
}) => {
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [monthlyReports, setMonthlyReports] = useState<MonthlyReport[]>([]);
  const [currentReport, setCurrentReport] = useState<string>('');
  const [replyContent, setReplyContent] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [isEditingReply, setIsEditingReply] = useState(false);
  const [availableYears, setAvailableYears] = useState<number[]>([new Date().getFullYear()]);
  const [availableMonths, setAvailableMonths] = useState<number[]>([]); // 리포트가 있는 월들

  const months = [
    { number: 1, name: '1월' },
    { number: 2, name: '2월' },
    { number: 3, name: '3월' },
    { number: 4, name: '4월' },
    { number: 5, name: '5월' },
    { number: 6, name: '6월' },
    { number: 7, name: '7월' },
    { number: 8, name: '8월' },
    { number: 9, name: '9월' },
    { number: 10, name: '10월' },
    { number: 11, name: '11월' },
    { number: 12, name: '12월' }
  ];

  useEffect(() => {
    if (isOpen && studentCode) {
      loadAvailableYears();
    }
  }, [isOpen, studentCode]);

  useEffect(() => {
    if (isOpen && selectedYear && !selectedMonth) {
      // 년도가 선택되었지만 월이 선택되지 않은 경우, 가장 최신 월을 자동 선택
      loadLatestMonth();
    }
  }, [isOpen, selectedYear, selectedMonth]);

  useEffect(() => {
    if (isOpen && selectedMonth && selectedYear) {
      loadMonthlyReport();
    }
  }, [isOpen, selectedMonth, selectedYear]);

  // 해당 학생의 기존 리포트 년도들 불러오기
  const loadAvailableYears = async () => {
    if (!studentCode) return;
    
    try {
      const reportsRef = ref(realtimeDb, `students/code/${studentCode}/reports`);
      const snapshot = await get(reportsRef);
      
      if (snapshot.exists()) {
        const reportsData = snapshot.val();
        const existingYears = Object.keys(reportsData).map(year => parseInt(year)).sort((a, b) => b - a); // 내림차순 정렬
        
        // 현재 년도가 없으면 추가
        const currentYear = new Date().getFullYear();
        if (!existingYears.includes(currentYear)) {
          existingYears.unshift(currentYear);
        }
        
        setAvailableYears(existingYears);
        setSelectedYear(existingYears[0]); // 가장 최근 년도 선택
        setSelectedMonth(null); // 월 선택 초기화하여 자동 선택 트리거
      } else {
        // 리포트가 없으면 현재 년도만
        const currentYear = new Date().getFullYear();
        setAvailableYears([currentYear]);
        setSelectedYear(currentYear);
        setSelectedMonth(null); // 월 선택 초기화하여 자동 선택 트리거
      }
    } catch (error) {
      console.error('기존 리포트 년도 불러오기 실패:', error);
      const currentYear = new Date().getFullYear();
      setAvailableYears([currentYear]);
      setSelectedYear(currentYear);
      setSelectedMonth(null); // 월 선택 초기화하여 자동 선택 트리거
    }
  };

  // 선택된 년도의 가장 최신 월을 찾아서 자동 선택
  const loadLatestMonth = async () => {
    if (!studentCode || !selectedYear) return;
    
    try {
      const reportsRef = ref(realtimeDb, `students/code/${studentCode}/reports/${selectedYear}`);
      const snapshot = await get(reportsRef);
      
      if (snapshot.exists()) {
        const reportsData = snapshot.val();
        const existingMonths = Object.keys(reportsData).map(month => {
          // 영어 월 이름을 숫자로 변환
          const monthMap: { [key: string]: number } = {
            'January': 1, 'February': 2, 'March': 3, 'April': 4,
            'May': 5, 'June': 6, 'July': 7, 'August': 8,
            'September': 9, 'October': 10, 'November': 11, 'December': 12
          };
          return monthMap[month] || 0;
        }).filter(month => month > 0).sort((a, b) => b - a); // 내림차순 정렬
        
        // 리포트가 있는 월들 저장
        setAvailableMonths(existingMonths);
        
        if (existingMonths.length > 0) {
          // 가장 최신 월 선택
          setSelectedMonth(existingMonths[0]);
        } else {
          // 해당 년도에 리포트가 없으면 현재 월 선택
          const currentMonth = new Date().getMonth() + 1;
          setSelectedMonth(currentMonth);
          setAvailableMonths([]);
        }
      } else {
        // 해당 년도에 리포트가 없으면 현재 월 선택
        const currentMonth = new Date().getMonth() + 1;
        setSelectedMonth(currentMonth);
        setAvailableMonths([]);
      }
    } catch (error) {
      console.error('최신 월 불러오기 실패:', error);
      // 에러 발생 시 현재 월 선택
      const currentMonth = new Date().getMonth() + 1;
      setSelectedMonth(currentMonth);
      setAvailableMonths([]);
    }
  };

  const loadMonthlyReport = async () => {
    if (!selectedMonth || !selectedYear) return;
    
    setLoading(true);
    try {
      let reportRef;
      
      if (userType === 'guest' && studentCode) {
        // guest 사용자: TeacherDashboard와 동일한 구조에서 조회
        // 한글 월을 영어로 변환하여 데이터베이스에서 조회
        const monthString = getEnglishMonthName(selectedMonth);
        reportRef = ref(realtimeDb, `students/code/${studentCode}/reports/${selectedYear}/${monthString}`);
      } else {
        // teacher, parent 사용자: 기존 구조에서 조회
        reportRef = ref(realtimeDb, `reports/${childClass}/${childName}/${selectedMonth}`);
      }
      
      const snapshot = await get(reportRef);
      
      if (snapshot.exists()) {
        const reportData = snapshot.val();
        
        if (userType === 'guest') {
          // guest 사용자용 데이터 구조
          const report: MonthlyReport = {
            month: selectedMonth,
            content: reportData.content || '',
            teacherId: reportData.teacherCode || '',
            teacherName: reportData.teacherName || '',
            createdAt: reportData.createdAt || '',
            reply: reportData.reply || '',
            replyAuthor: reportData.replyAuthor || '',
            replyCreatedAt: reportData.replyCreatedAt || ''
          };
          setMonthlyReports([report]);
          setCurrentReport(reportData.content || '');
          setReplyContent(reportData.reply || '');
        } else {
          // 기존 사용자용 데이터 구조
          const report: MonthlyReport = {
            month: selectedMonth,
            content: reportData.content || '',
            teacherId: reportData.teacherCode || '',
            teacherName: reportData.teacherName || '',
            createdAt: reportData.createdAt || '',
            reply: reportData.reply || '',
            replyAuthor: reportData.replyAuthor || '',
            replyCreatedAt: reportData.replyCreatedAt || ''
          };
          setMonthlyReports([report]);
          setCurrentReport(reportData.content || '');
          setReplyContent(reportData.reply || '');
        }
      } else {
        // 리포트가 없는 경우
        setMonthlyReports([]);
        setCurrentReport('');
        setReplyContent('');
      }
    } catch (error) {
      console.error('월간 리포트 불러오기 실패:', error);
      setMonthlyReports([]);
      setCurrentReport('');
      setReplyContent('');
    } finally {
      setLoading(false);
    }
  };

  // 년도 변경 시 최신 월 자동 선택
  const handleYearChange = (year: number) => {
    setSelectedYear(year);
    setSelectedMonth(null); // 월 선택 초기화하여 자동 선택 트리거
  };

  // 월 선택 시 리포트 로드
  const handleMonthSelect = (month: number) => {
    setSelectedMonth(month);
  };

  // 해당 월에 리포트가 있는지 확인
  const hasReport = (monthNumber: number) => {
    return availableMonths.includes(monthNumber);
  };

  // 월별 리포트 표시 함수
  const getMonthDisplayName = (monthNumber: number) => {
    const monthNames = [
      '1월', '2월', '3월', '4월', '5월', '6월',
      '7월', '8월', '9월', '10월', '11월', '12월'
    ];
    return monthNames[monthNumber - 1];
  };

  // TeacherDashboard에서 사용하는 영어 월 이름으로 변환
  const getEnglishMonthName = (monthNumber: number) => {
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return monthNames[monthNumber - 1];
  };

  const handleSaveReport = async () => {
    if (!selectedMonth || !currentReport.trim()) return;

    try {
      const reportRef = ref(realtimeDb, `reports/${childClass}/${childName}/${selectedMonth}`);
      const newReport: MonthlyReport = {
        month: selectedMonth,
        content: currentReport.trim(),
        teacherId: userId,
        teacherName: '선생님', // 실제로는 사용자 데이터에서 가져와야 함
        createdAt: new Date().toISOString(),
        reply: '',
        replyAuthor: '',
        replyCreatedAt: ''
      };

      await set(reportRef, newReport);
      setMonthlyReports([newReport]);
      alert('리포트가 저장되었습니다.');
    } catch (error) {
      console.error('리포트 저장 실패:', error);
      alert('리포트 저장에 실패했습니다.');
    }
  };

  const handleSaveReply = async () => {
    if (!selectedMonth || !replyContent.trim()) return;

    try {
      let reportRef;
      
      if (userType === 'guest' && studentCode) {
        // guest 사용자: TeacherDashboard와 동일한 구조에 저장 (영어 월 이름 사용)
        const monthString = getEnglishMonthName(selectedMonth);
        reportRef = ref(realtimeDb, `students/code/${studentCode}/reports/${selectedYear}/${monthString}`);
      } else {
        // 기존 사용자: 기존 구조에 저장
        reportRef = ref(realtimeDb, `reports/${childClass}/${childName}/${selectedMonth}`);
      }

      // 기존 리포트가 있는지 확인
      const snapshot = await get(reportRef);
      if (!snapshot.exists()) {
        alert('먼저 선생님이 리포트를 작성해야 합니다.');
        return;
      }

      const existingReport = snapshot.val();
      const isExistingReply = !!(existingReport.reply);
      
      // 학부모 한마디 정보 업데이트
      const replyData: any = {
        reply: replyContent.trim(),
        replyAuthor: userType === 'parent' ? '학부모' : '사용자',
      };
      
      // 기존 답장이 있으면 수정이므로 replyUpdatedAt 설정, 없으면 새로 작성이므로 replyCreatedAt 설정
      if (isExistingReply) {
        replyData.replyUpdatedAt = new Date().toISOString();
        // replyCreatedAt은 기존 값 유지
        if (existingReport.replyCreatedAt) {
          replyData.replyCreatedAt = existingReport.replyCreatedAt;
        }
      } else {
        replyData.replyCreatedAt = new Date().toISOString();
      }

      await update(reportRef, replyData);
      
      // 답장 상태 계산 및 저장 (guest 사용자인 경우만, 모든 년도/월 확인)
      if (userType === 'guest' && studentCode) {
        // 모든 년도/월의 리포트 확인하여 전체 상태 계산
        const allReportsRef = ref(realtimeDb, `students/code/${studentCode}/reports`);
        const allReportsSnapshot = await get(allReportsRef);
        
        let overallStatus: 'new' | 'updated' | 'read' = 'read';
        let hasNewReply = false;
        let hasUpdatedReply = false;
        
        if (allReportsSnapshot.exists()) {
          const allReportsData = allReportsSnapshot.val();
          
          // 모든 년도 확인
          for (const year in allReportsData) {
            const yearData = allReportsData[year];
            
            // 모든 월 확인
            for (const month in yearData) {
              const report = yearData[month];
              
              // 답장 상태 계산
              let monthStatus: 'none' | 'new' | 'updated' | 'read' = 'none';
              if (report.reply) {
                if (!report.teacherReadAt) {
                  monthStatus = 'new';
                } else if (report.replyUpdatedAt && report.teacherReadAt) {
                  const replyUpdatedTime = new Date(report.replyUpdatedAt).getTime();
                  const teacherReadTime = new Date(report.teacherReadAt).getTime();
                  if (replyUpdatedTime > teacherReadTime) {
                    monthStatus = 'updated';
                  } else {
                    monthStatus = 'read';
                  }
                } else {
                  monthStatus = 'read';
                }
              }
              
              // 전체 상태 결정 (최우선순위: new > updated > read)
              if (monthStatus === 'new') {
                hasNewReply = true;
                break; // 새 답장이 있으면 즉시 중단
              } else if (monthStatus === 'updated') {
                hasUpdatedReply = true;
                // break하지 않고 계속 확인 (새 답장이 더 우선순위가 높으므로)
              }
            }
            
            // 새 답장이 있으면 더 이상 확인할 필요 없음
            if (hasNewReply) {
              break;
            }
          }
          
          // 전체 상태 결정
          if (hasNewReply) {
            overallStatus = 'new';
          } else if (hasUpdatedReply) {
            overallStatus = 'updated';
          } else {
            overallStatus = 'read';
          }
        }
        
        // 전체 상태 저장 (년도 구분 없이 하나만)
        const statusRef = ref(realtimeDb, `students/code/${studentCode}/replyStatus`);
        await set(statusRef, overallStatus);
      }
      
      // 로컬 상태 업데이트
      const updatedReports = monthlyReports.map(report => 
        report.month === selectedMonth 
          ? { ...report, ...replyData }
          : report
      );
      setMonthlyReports(updatedReports);
      
      setIsEditingReply(false);
      alert('학부모 한마디가 저장되었습니다.');
    } catch (error) {
      console.error('학부모 한마디 저장 실패:', error);
      alert('학부모 한마디 저장에 실패했습니다.');
    }
  };

  const handleEditReply = () => {
    setIsEditingReply(true);
  };

  const handleCancelEditReply = () => {
    setIsEditingReply(false);
    // 원래 학부모 한마디 내용으로 복원
    if (monthlyReports.length > 0) {
      setReplyContent(monthlyReports[0].reply || '');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content report-card-modal">
        <div className="modal-header">
          <h2 className="modal-title">월별 리포트 - {childName} ({childClass})</h2>
          <button className="modal-close-button" onClick={onClose}>
            ×
          </button>
        </div>
        
        <div className="modal-body">
          {/* 년도 선택 (guest 사용자만) */}
          {userType === 'guest' && (
            <div className="year-selection">
              <h3>년도 선택</h3>
              <select
                value={selectedYear}
                onChange={(e) => handleYearChange(parseInt(e.target.value))}
                className="year-select"
              >
                {availableYears.map(year => (
                  <option key={year} value={year}>{year}년</option>
                ))}
              </select>
            </div>
          )}

          {/* 월 선택 버튼들 */}
          <div className="month-selection">
            <h3>월 선택 {selectedMonth && <span className="latest-indicator">(최신 리포트 자동 선택됨)</span>}</h3>
            <div className="month-buttons">
              {months.map(month => (
                <button
                  key={month.number}
                  onClick={() => handleMonthSelect(month.number)}
                  className={`month-button ${selectedMonth === month.number ? 'active' : ''} ${hasReport(month.number) ? 'has-report' : ''}`}
                >
                  {month.name}
                </button>
              ))}
            </div>
          </div>

          {/* 선택된 월의 리포트 내용 */}
          {selectedMonth && (
            <div className="monthly-report">
              <h3>{getMonthDisplayName(selectedMonth)} 리포트</h3>
              
              {loading ? (
                <div className="loading">로딩 중...</div>
              ) : monthlyReports.length > 0 ? (
                <div className="report-content">
                  <div className="report-header">
                    <span className="report-date">
                      작성일: {new Date(monthlyReports[0].createdAt).toLocaleDateString()}
                    </span>
                    <span className="report-author">
                      작성자: {monthlyReports[0].teacherName}
                    </span>
                  </div>
                  
                  <h4 className="teacher-comment-title">Teacher's comment</h4>
                  <div className="report-text">
                    {monthlyReports[0].content}
                  </div>

                  {/* 선생님인 경우 편집 가능 */}
                  {userType === 'teacher' && (
                    <div className="teacher-edit">
                      <h4>리포트 편집</h4>
                      <textarea
                        value={currentReport}
                        onChange={(e) => setCurrentReport(e.target.value)}
                        placeholder="리포트 내용을 입력하세요..."
                        rows={6}
                        className="report-textarea"
                      />
                      <button 
                        onClick={handleSaveReport}
                        className="save-button"
                        disabled={!currentReport.trim()}
                      >
                        저장
                      </button>
                    </div>
                  )}

                  {/* 학부모 한마디 섹션 */}
                  <div className="replies-section">
                    <h4>학부모 한마디</h4>
                    
                    {/* 기존 학부모 한마디 표시 */}
                    {monthlyReports[0].reply ? (
                      <div className="reply-item">
                        <div className="reply-header">
                          <span className="reply-author">{monthlyReports[0].replyAuthor}</span>
                          <span className="reply-date">
                            {new Date(monthlyReports[0].replyCreatedAt || '').toLocaleDateString()}
                          </span>
                        </div>
                        <div className="reply-content">{monthlyReports[0].reply}</div>
                      </div>
                    ) : (
                      <p className="no-replies">아직 학부모 한마디가 없습니다.</p>
                    )}

                    {/* 방문자나 학부모인 경우 학부모 한마디 작성/수정 가능 */}
                    {(userType === 'parent' || userType === 'guest') && (
                      <div className="reply-form">
                        <h4>{monthlyReports[0].reply ? '학부모 한마디 수정' : '학부모 한마디 작성'}</h4>
                        <textarea
                          value={replyContent}
                          onChange={(e) => setReplyContent(e.target.value)}
                          placeholder="학부모 한마디를 입력하세요..."
                          rows={4}
                          className="reply-textarea"
                        />
                        <div className="reply-actions">
                          {monthlyReports[0].reply && !isEditingReply ? (
                            <button 
                              onClick={handleEditReply}
                              className="edit-reply-button"
                            >
                              수정
                            </button>
                          ) : (
                            <>
                              <button 
                                onClick={handleSaveReply}
                                className="reply-button"
                                disabled={!replyContent.trim()}
                              >
                                {monthlyReports[0].reply ? '수정 완료' : '학부모 한마디 작성'}
                              </button>
                              {monthlyReports[0].reply && (
                                <button 
                                  onClick={handleCancelEditReply}
                                  className="cancel-edit-button"
                                >
                                  취소
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="no-report">
                  <p>이번 달 리포트가 아직 작성되지 않았습니다.</p>
                  
                  {/* 선생님인 경우 새로 작성 가능 */}
                  {userType === 'teacher' && (
                    <div className="teacher-create">
                      <h4>새 리포트 작성</h4>
                      <textarea
                        value={currentReport}
                        onChange={(e) => setCurrentReport(e.target.value)}
                        placeholder="리포트 내용을 입력하세요..."
                        rows={6}
                        className="report-textarea"
                      />
                      <button 
                        onClick={handleSaveReport}
                        className="save-button"
                        disabled={!currentReport.trim()}
                      >
                        저장
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="modal-button" onClick={onClose}>
            닫기
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportCardModal;
